import type { EnvironmentId } from "@cmmd-forge/contracts";

import type { CloudRuntimeView } from "./DiagnosticsCloudRuntimes.logic";
import type { DesktopDiagnosticsTarget } from "./DiagnosticsDesktopTabs.logic";
import type { DiagnosticsTab, StatusTone } from "./DiagnosticsSettings.types";

/**
 * One diagnostics target is one machine: the Forge server this browser is
 * talking to, a CMMD Cloud VM (sprite), or a trusted, currently-online Forge
 * Desktop device. The picker exists because these used to be selected by
 * different controls — devices by a tab per device in the header strip, sprites
 * by a list inside the Cloud panel, and the server not at all — so "which
 * machine am I looking at" had no single answer.
 */
export type DiagnosticsTargetKind = "server" | "cloud" | "desktop";

/**
 * What kind of evidence the page can actually offer for a target. The three
 * differ enormously, and a panel that does not say so lets an empty section
 * read as "nothing wrong here" when it means "we cannot see from here".
 */
export type DiagnosticsEvidenceClass = "direct" | "control-plane" | "registry";

export interface DiagnosticsTargetOption {
  /** Stable identity for the option; also what the menu keys and compares on. */
  readonly key: string;
  readonly kind: DiagnosticsTargetKind;
  /** The tab this option activates. */
  readonly tab: DiagnosticsTab;
  /** Set for cloud targets only; selecting one also selects it in the Cloud panel. */
  readonly environmentId: EnvironmentId | null;
  readonly label: string;
  /** Secondary line — lifecycle for sprites, platform for devices. */
  readonly detail: string;
  readonly tone: StatusTone;
  readonly evidence: DiagnosticsEvidenceClass;
}

export interface DiagnosticsTargetGroup {
  readonly id: DiagnosticsTargetKind;
  readonly label: string;
  readonly options: ReadonlyArray<DiagnosticsTargetOption>;
}

export const DIAGNOSTICS_EVIDENCE_COPY: Readonly<
  Record<DiagnosticsEvidenceClass, { readonly label: string; readonly description: string }>
> = {
  direct: {
    label: "Direct",
    description:
      "Read from this server's own process table, tool checks, and trace file. Live, and as complete as diagnostics get.",
  },
  "control-plane": {
    label: "Control plane",
    description:
      "Observed through CMMD, not the Environment itself. Passive by design: reading this never wakes a suspended sprite, so it reports what CMMD last recorded.",
  },
  registry: {
    label: "Registry only",
    description:
      "CMMD knows this device is authorized and online. Forge cannot read its processes, tools, or traces from here — open Diagnostics on the device itself for those.",
  },
};

const SERVER_TARGET_KEY = "server";

/**
 * What the server target is on this surface, if it is anything.
 *
 * - `server` — the container is the user's own machine (desktop, local web).
 *   It carries the environment's own label.
 * - `cloud-surface` — the shared Forge Cloud control container. Same evidence,
 *   different owner, so it gets a name that says whose it is.
 * - `hidden` — the shared container, viewed by someone who does not run the
 *   platform. Not a target.
 */
export type ServerDiagnosticsTarget = "server" | "cloud-surface" | "hidden";

/**
 * Owning the session is the right test only where the session owner also owns
 * the machine. On the shared cloud shell they are different people: every
 * signed-in user owns a session there and none of them owns the container, so
 * the test becomes whether they run the platform.
 *
 * This is deliberately not workspace admin. Owning a workspace makes someone
 * an admin of their own org, which says nothing about the container CMMD runs
 * Forge Cloud on.
 *
 * The server enforces the same rule on `/api/admin/runtime/diagnostics`; this
 * decides what to *offer*, and is not what keeps the data safe.
 */
export function resolveServerDiagnosticsTarget(input: {
  readonly sharedControlSurface: boolean;
  readonly isPlatformAdmin: boolean;
}): ServerDiagnosticsTarget {
  if (!input.sharedControlSurface) return "server";
  return input.isPlatformAdmin ? "cloud-surface" : "hidden";
}

const CLOUD_SURFACE_LABEL = "Cloud Surface";

function cloudTargetKey(environmentId: EnvironmentId): string {
  return `cloud:${String(environmentId)}`;
}

/**
 * A sprite the control plane calls `running` is reachable; anything else —
 * provisioning, suspended, destroyed — is not something to read as healthy.
 */
function cloudTone(catalogStatus: CloudRuntimeView["catalogStatus"]): StatusTone {
  return catalogStatus === "running" ? "ready" : "muted";
}

export function makeDiagnosticsTargetOptions(input: {
  readonly serverLabel: string;
  /**
   * Required rather than defaulted: a default here would decide, silently and
   * for every caller, whether the shared control container is on offer.
   */
  readonly serverTarget: ServerDiagnosticsTarget;
  readonly cloudRuntimes: ReadonlyArray<CloudRuntimeView>;
  readonly desktopTargets: ReadonlyArray<DesktopDiagnosticsTarget>;
}): ReadonlyArray<DiagnosticsTargetGroup> {
  // First when it is offered at all: it is the one machine the page is
  // guaranteed to be able to reach, because the page is served by it.
  const isCloudSurface = input.serverTarget === "cloud-surface";
  const server: ReadonlyArray<DiagnosticsTargetOption> =
    input.serverTarget === "hidden"
      ? []
      : [
          {
            key: SERVER_TARGET_KEY,
            kind: "server" as const,
            tab: "server" as const,
            environmentId: null,
            label: isCloudSurface ? CLOUD_SURFACE_LABEL : input.serverLabel,
            detail: isCloudSurface ? "shared control container" : "serving this page",
            tone: "ready" as const,
            evidence: "direct" as const,
          },
        ];
  const cloud: ReadonlyArray<DiagnosticsTargetOption> = input.cloudRuntimes.map((runtime) => ({
    key: cloudTargetKey(runtime.environmentId),
    kind: "cloud" as const,
    tab: "cloud" as const,
    environmentId: runtime.environmentId,
    label: runtime.label,
    detail: runtime.catalogStatus,
    tone: cloudTone(runtime.catalogStatus),
    evidence: "control-plane" as const,
  }));
  const desktop: ReadonlyArray<DiagnosticsTargetOption> = input.desktopTargets.map((target) => ({
    key: target.tabId,
    kind: "desktop" as const,
    tab: target.tabId,
    environmentId: null,
    label: target.label,
    // Online is the expected state, so the platform is the more useful second
    // line there; offline is the fact worth reading before clicking.
    detail: target.presence === "online" ? target.platform : "offline",
    tone: target.presence === "online" ? ("ready" as const) : ("muted" as const),
    evidence: "registry" as const,
  }));
  const groups: ReadonlyArray<DiagnosticsTargetGroup> = [
    {
      id: "server",
      label: isCloudSurface ? CLOUD_SURFACE_LABEL : "This server",
      options: server,
    },
    { id: "cloud", label: "Environments", options: cloud },
    { id: "desktop", label: "Desktop devices", options: desktop },
  ];
  return groups.filter((group) => group.options.length > 0);
}

export function flattenDiagnosticsTargets(
  groups: ReadonlyArray<DiagnosticsTargetGroup>,
): ReadonlyArray<DiagnosticsTargetOption> {
  return groups.flatMap((group) => group.options);
}

/**
 * Cloud is one tab shared by every sprite, so the active cloud option is
 * whichever sprite the Cloud panel currently holds — not the tab alone. With no
 * sprite selected there is no active target, and the trigger says so.
 */
export function activeDiagnosticsTargetKey(input: {
  readonly activeTab: DiagnosticsTab;
  readonly selectedEnvironmentId: EnvironmentId | null;
}): string | null {
  if (input.activeTab === "events") return null;
  if (input.activeTab === "server") return SERVER_TARGET_KEY;
  if (input.activeTab === "cloud") {
    return input.selectedEnvironmentId === null
      ? null
      : cloudTargetKey(input.selectedEnvironmentId);
  }
  return input.activeTab;
}

export function findDiagnosticsTarget(
  groups: ReadonlyArray<DiagnosticsTargetGroup>,
  key: string | null,
): DiagnosticsTargetOption | null {
  if (key === null) return null;
  return flattenDiagnosticsTargets(groups).find((option) => option.key === key) ?? null;
}

export function diagnosticsTargetTriggerLabel(active: DiagnosticsTargetOption | null): string {
  return active?.label ?? "Select a target";
}
